import { cva, type VariantProps } from '@veraclins-dev/cva';

import { styleProps } from './styles';

/** ::::::::: List Item ::::::::: */
const listItemVariants = cva({
  base: 'relative group-data-[marker=none]/list:flex',
  responsive: {
    ...styleProps,
  },
  variants: {
    variant: {
      default: '',
      // A row that explains the list rather than offering a choice: the
      // Autocomplete's "No options found", its free-solo hint and its
      // wait-for-the-parent-field message. It reads as body copy, so it takes
      // the muted text token and no pointer affordance. These rows used to be
      // `selectable` and `disabled`, which stacked `opacity-50` over
      // `text-foreground/80` and dropped the message to between 2.6:1 and
      // 3.5:1, under the 4.5:1 WCAG 1.4.3 holds text to.
      message: 'cursor-default text-neutral-muted-foreground',
      clickable:
        'cursor-pointer transition-colors hover:bg-neutral-hover hover:text-neutral-foreground active:bg-neutral-hover active:text-neutral-foreground focus:bg-neutral-hover focus:text-neutral-foreground focus:outline-hidden',
      selectable:
        'cursor-pointer transition-colors focus:outline-hidden focus:bg-neutral-hover focus:text-neutral-foreground hover:bg-neutral-hover hover:text-neutral-foreground active:bg-neutral-hover active:text-neutral-foreground',
    },
    itemSize: {
      sm: 'text-xs',
      md: 'text-sm',
      lg: 'text-lg',
      xl: 'text-xl',
    },
    weight: {
      normal: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
    },
    tone: {
      default: '',
      danger: 'text-danger',
      success: 'text-success',
      warning: 'text-warning-muted-foreground',
      info: 'text-info',
      neutral: 'text-neutral',
    },
    // A visual-only surface for a row that needs attention, such as an unread
    // notification. It sets no ARIA state, so it is not announced as selected.
    highlighted: {
      true: 'bg-primary-muted',
      false: '',
    },
    selected: {
      true: 'bg-neutral text-neutral-foreground',
      false: '',
    },
    focused: {
      true: 'bg-neutral-hover text-neutral-foreground',
      false: '',
    },
    disabled: {
      true: 'opacity-50 cursor-not-allowed pointer-events-none',
      false: '',
    },
  },
  compoundVariants: [
    // Focus and selection stay distinguishable from a highlighted row: focus
    // shows the neutral hover surface, selection shows the neutral surface.
    // Hover on clickable/selectable rows is a `hover:` class, which sits after
    // `bg-primary-muted` in the generated CSS and wins while the pointer is over.
    {
      highlighted: true,
      focused: true,
      className: 'bg-neutral-hover text-neutral-foreground',
    },
    {
      highlighted: true,
      selected: true,
      className: 'bg-neutral text-neutral-foreground',
    },
    // Handle conflicts - selected takes precedence over focused
    {
      selected: true,
      focused: true,
      className: 'bg-neutral text-neutral-foreground',
    },
    // Disabled state overrides other states
    {
      disabled: true,
      selected: true,
      className:
        'opacity-50 cursor-not-allowed pointer-events-none bg-neutral text-neutral-foreground',
    },
    {
      disabled: true,
      focused: true,
      className:
        'opacity-50 cursor-not-allowed pointer-events-none bg-neutral-hover text-neutral-foreground',
    },
    // Disable interactive variants when disabled
    {
      disabled: true,
      variant: 'clickable',
      className: 'opacity-50 cursor-not-allowed pointer-events-none',
    },
    {
      disabled: true,
      variant: 'selectable',
      className: 'opacity-50 cursor-not-allowed pointer-events-none',
    },
  ],
  defaultVariants: {
    variant: 'default',
    itemSize: 'md',
    weight: 'normal',
    tone: 'default',
    highlighted: false,
    selected: false,
    focused: false,
    disabled: false,
  },
});

type ListItemVariants = VariantProps<typeof listItemVariants>;

export { type ListItemVariants, listItemVariants };
